/* ============================================================
   Supply module — UI kit
   ------------------------------------------------------------
   Self-contained primitives + icons for the Supply screens,
   ported from the Caliper "CureBay Operator Console" UI kit.
   Styling rides on the Caliper design tokens already loaded
   app-wide (src/styles/caliper-tokens.css).
   ============================================================ */
import React from "react";
import moment from "moment";
import * as Popover from "@radix-ui/react-popover";
import {
  Download,
  Plus,
  X,
  Check,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Truck,
  Package,
  Lock,
  BookOpen,
  Receipt,
  Barcode,
  Filter,
  Clock,
  User,
  ArrowRight,
  Search,
  Inbox,
  Loader2,
  Eye,
  Pencil,
} from "lucide-react";

/* ---------- icons (Tabler-thin lucide wrappers) ---------- */
const mkIcon = (Cmp) => {
  const Icon = ({ size = 16, strokeWidth = 1.6, ...rest }) => (
    <Cmp size={size} strokeWidth={strokeWidth} {...rest} />
  );
  Icon.displayName = `Icon(${Cmp.displayName || Cmp.name || "lucide"})`;
  return Icon;
};
export const IconDownload = mkIcon(Download);
export const IconPlus = mkIcon(Plus);
export const IconX = mkIcon(X);
export const IconCheck = mkIcon(Check);
export const IconChevRight = mkIcon(ChevronRight);
export const IconChevDown = mkIcon(ChevronDown);
export const IconAlert = mkIcon(AlertTriangle);
export const IconTruck = mkIcon(Truck);
export const IconPackage = mkIcon(Package);
export const IconLock = mkIcon(Lock);
export const IconBook = mkIcon(BookOpen);
export const IconReceipt = mkIcon(Receipt);
export const IconBarcode = mkIcon(Barcode);
export const IconFilter = mkIcon(Filter);
export const IconClock = mkIcon(Clock);
export const IconUser = mkIcon(User);
export const IconArrowRight = mkIcon(ArrowRight);
export const IconSearch = mkIcon(Search);
export const IconEye = mkIcon(Eye);
export const IconEdit = mkIcon(Pencil);

/* ---------- Button ---------- */
export const Button = ({
  variant = "secondary",
  size = "md",
  icon,
  children,
  onClick,
  disabled,
  type,
  style = {},
}) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    lineHeight: 1,
    border: "1px solid transparent",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background var(--dur-quick) var(--ease-out), border-color var(--dur-quick) var(--ease-out)",
    opacity: disabled ? 0.45 : 1,
    whiteSpace: "nowrap",
    borderRadius: 4,
  };
  const sizes = {
    sm: { height: 24, padding: "0 8px", fontSize: 12, borderRadius: 3 },
    md: { height: 30, padding: "0 12px", fontSize: 13 },
    lg: { height: 38, padding: "0 16px", fontSize: 14 },
  };
  const variants = {
    primary: { background: "var(--ink-600)", color: "var(--fg-on-ink)" },
    secondary: { background: "var(--neutral-0)", color: "var(--fg-strong)", borderColor: "var(--border-default)" },
    ghost: { background: "transparent", color: "var(--fg-default)" },
    danger: { background: "var(--red-500)", color: "#fff" },
  };
  return (
    <button
      type={type || "button"}
      onClick={onClick}
      disabled={disabled}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      onMouseOver={(e) => {
        if (disabled) return;
        if (variant === "primary") e.currentTarget.style.background = "var(--ink-700)";
        if (variant === "secondary" || variant === "ghost") e.currentTarget.style.background = "var(--bg-hover)";
        if (variant === "danger") e.currentTarget.style.background = "var(--red-700)";
      }}
      onMouseOut={(e) => {
        Object.assign(e.currentTarget.style, { background: "", borderColor: "" }, variants[variant]);
      }}
    >
      {icon}
      {children}
    </button>
  );
};

/* ---------- Pill ---------- */
export const Pill = ({ variant = "neutral", dot, children, style = {} }) => {
  const variants = {
    neutral: { bg: "var(--neutral-75)", fg: "var(--neutral-600)", dot: "var(--neutral-400)" },
    fresh: { bg: "var(--status-fresh-bg)", fg: "var(--status-fresh-fg)", dot: "var(--status-fresh-dot)" },
    soon: { bg: "var(--status-soon-bg)", fg: "var(--status-soon-fg)", dot: "var(--status-soon-dot)" },
    expired: { bg: "var(--status-expired-bg)", fg: "var(--status-expired-fg)", dot: "var(--status-expired-dot)" },
    transfer: { bg: "var(--status-transfer-bg)", fg: "var(--status-transfer-fg)", dot: "var(--status-transfer-dot)" },
    ink: { bg: "var(--ink-50)", fg: "var(--ink-700)", dot: "var(--ink-500)" },
    green: { bg: "var(--green-50)", fg: "var(--green-700)", dot: "var(--green-500)" },
    red: { bg: "var(--red-50)", fg: "var(--red-700)", dot: "var(--red-500)" },
    amber: { bg: "var(--amber-50)", fg: "var(--amber-700)", dot: "var(--amber-500)" },
  };
  const v = variants[variant] || variants.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        height: 20,
        padding: "0 7px",
        borderRadius: 3,
        background: v.bg,
        color: v.fg,
        fontFamily: "var(--font-sans)",
        fontWeight: 600,
        fontSize: 11,
        lineHeight: 1,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: 99, background: v.dot }} />}
      {children}
    </span>
  );
};

/* ---------- Input ---------- */
export const Input = ({ value, onChange, mono, prefix, suffix, error, style = {}, ...rest }) => (
  <div
    style={{
      display: "flex",
      alignItems: "stretch",
      border: error ? "1px solid var(--red-500)" : "1px solid var(--border-default)",
      boxShadow: error ? "var(--shadow-focus-danger)" : "none",
      borderRadius: 4,
      background: "var(--bg-panel)",
      overflow: "hidden",
      ...style,
    }}
  >
    {prefix && (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 8px",
          background: "var(--bg-sunken)",
          borderRight: "1px solid var(--border-subtle)",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--fg-muted)",
        }}
      >
        {prefix}
      </span>
    )}
    <input
      value={value}
      onChange={onChange}
      {...rest}
      style={{
        border: 0,
        outline: 0,
        background: "transparent",
        flex: 1,
        minWidth: 0,
        padding: "0 10px",
        height: 28,
        fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
        fontSize: mono ? 12 : 13,
        color: "var(--fg-default)",
      }}
    />
    {suffix && (
      <span style={{ display: "flex", alignItems: "center", padding: "0 8px", color: "var(--fg-muted)", fontSize: 11 }}>
        {suffix}
      </span>
    )}
  </div>
);

/* ---------- Select ---------- */
export const Select = ({ style = {}, children, ...rest }) => (
  <select
    {...rest}
    style={{
      height: 28,
      padding: "0 10px",
      border: "1px solid var(--border-default)",
      borderRadius: 4,
      fontSize: 12,
      fontFamily: "var(--font-sans)",
      background: "var(--bg-panel)",
      color: "var(--fg-default)",
      cursor: "pointer",
      maxWidth: "100%",
      ...style,
    }}
  >
    {children}
  </select>
);

/* ---------- Searchable single-select ----------
   A dropdown that filters its options as you type — for lists long enough that a
   plain <select> is awkward to scan (e.g. eClinic). `options` is
   [{ value, label }]; `value` is the selected value; `onChange` receives the new
   value string. Chrome matches the plain <Select>. */
export const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  style = {},
  "aria-label": ariaLabel,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const ref = React.useRef(null);

  const selected = options.find((o) => o.value === value);

  /* Close when clicking outside the control. */
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => (o.label || "").toLowerCase().includes(q))
    : options;

  const choose = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          width: "100%",
          height: 28,
          padding: "0 10px",
          border: "1px solid var(--border-default)",
          borderRadius: 4,
          fontSize: 12,
          fontFamily: "var(--font-sans)",
          background: "var(--bg-panel)",
          color: selected ? "var(--fg-default)" : "var(--fg-muted)",
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} strokeWidth={1.6} style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 50,
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 6, borderBottom: "1px solid var(--border-default)" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <Search
                size={13}
                strokeWidth={1.6}
                style={{ position: "absolute", left: 8, color: "var(--fg-muted)" }}
              />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                style={{
                  width: "100%",
                  height: 26,
                  padding: "0 8px 0 26px",
                  border: "1px solid var(--border-default)",
                  borderRadius: 4,
                  fontSize: 12,
                  fontFamily: "var(--font-sans)",
                  background: "var(--bg-panel)",
                  color: "var(--fg-default)",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto", padding: 4 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--fg-muted)" }}>
                No matches
              </div>
            ) : (
              filtered.map((o) => {
                const active = o.value === value;
                return (
                  <button
                    key={o.value || "__all"}
                    type="button"
                    onClick={() => choose(o.value)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "6px 10px",
                      border: 0,
                      borderRadius: 4,
                      fontSize: 12,
                      fontFamily: "var(--font-sans)",
                      background: active ? "var(--ink-50, #eef2ff)" : "transparent",
                      color: "var(--fg-default)",
                      fontWeight: active ? 600 : 400,
                      cursor: "pointer",
                    }}
                  >
                    {o.label}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Date-range filter ----------
   Shared single-control date-range picker used by every Supply list toolbar so
   the date filter looks and behaves identically across the module. One button
   opens a calendar popover; click a start day then an end day to set the range.
   Both bounds are optional (cleared = no date filter). `from`/`to` are
   YYYY-MM-DD strings; `onFromChange`/`onToChange` receive the same. */
const DATE_FMT = "YYYY-MM-DD";
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/* ---------- Accessible calendar grid ----------
   Month nav + weekday header + a keyboard-navigable day grid. Owns its own
   `view` and roving-focus state (seeded from `seedISO` on mount), so it resets
   cleanly each time a popover reopens. Range highlighting is driven by the
   `fromM`/`toM` the parent passes; day selection is reported via `onPick`.

   Keyboard: arrows move by day/week, Home/End jump to week edges, PageUp/Down
   change month, Enter/Space select. DOM focus follows the roving index. */
const CalendarGrid = ({ seedISO, fromM, toM, onPick, maxM }) => {
  const seedM = seedISO ? moment(seedISO, DATE_FMT) : moment();
  const [view, setView] = React.useState(() => seedM.clone().startOf("month"));
  const [focusedISO, setFocusedISO] = React.useState(() => seedM.format(DATE_FMT));
  const gridRef = React.useRef(null);

  const focusedM = moment(focusedISO, DATE_FMT);

  /* Future days (after `maxM`) are non-selectable. */
  const isDisabled = (d) => maxM && d.isAfter(maxM, "day");

  /* Keep DOM focus on the active day as the roving index moves (and on open). */
  React.useEffect(() => {
    const el = gridRef.current?.querySelector(`[data-date="${focusedISO}"]`);
    if (el) el.focus();
  }, [focusedISO]);

  const gridStart = view.clone().startOf("month").startOf("week");
  const days = Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, "day"));
  const weeks = Array.from({ length: 6 }, (_, i) => days.slice(i * 7, i * 7 + 7));
  const inRange = (d) => fromM && toM && d.isBetween(fromM, toM, "day", "[]");
  const isEnd = (d) => (fromM && d.isSame(fromM, "day")) || (toM && d.isSame(toM, "day"));

  const goMonth = (delta) => {
    const nv = view.clone().add(delta, "month");
    setView(nv);
    setFocusedISO(nv.clone().startOf("month").format(DATE_FMT));
  };

  /* Move the roving focus to `next`, following it into an adjacent month. */
  const focusTo = (next) => {
    setFocusedISO(next.format(DATE_FMT));
    if (!next.isSame(view, "month")) setView(next.clone().startOf("month"));
  };

  const onKeyDown = (e) => {
    switch (e.key) {
      case "ArrowLeft": focusTo(focusedM.clone().subtract(1, "day")); break;
      case "ArrowRight": focusTo(focusedM.clone().add(1, "day")); break;
      case "ArrowUp": focusTo(focusedM.clone().subtract(7, "day")); break;
      case "ArrowDown": focusTo(focusedM.clone().add(7, "day")); break;
      case "Home": focusTo(focusedM.clone().startOf("week")); break;
      case "End": focusTo(focusedM.clone().endOf("week")); break;
      case "PageUp": focusTo(focusedM.clone().subtract(1, "month")); break;
      case "PageDown": focusTo(focusedM.clone().add(1, "month")); break;
      case "Enter":
      case " ":
        if (!isDisabled(focusedM)) onPick(focusedM.clone());
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  return (
    <div>
      {/* Month nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <CalNavBtn onClick={() => goMonth(-1)} label="Previous month">
          <ChevronLeft size={16} strokeWidth={1.6} />
        </CalNavBtn>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-strong)" }}>
          {view.format("MMMM YYYY")}
        </span>
        <CalNavBtn onClick={() => goMonth(1)} label="Next month">
          <ChevronRight size={16} strokeWidth={1.6} />
        </CalNavBtn>
      </div>

      {/* Weekday header — decorative; each day cell carries its own full label. */}
      <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 2 }}>
        {WEEKDAYS.map((w) => (
          <span key={w} style={{ textAlign: "center", fontSize: 10, color: "var(--fg-muted)", fontWeight: 600 }}>
            {w}
          </span>
        ))}
      </div>

      {/* Day grid */}
      <div ref={gridRef} role="grid" aria-label="Calendar" onKeyDown={onKeyDown}>
        {weeks.map((week, wi) => (
          <div key={wi} role="row" style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1 }}>
            {week.map((d) => {
              const iso = d.format(DATE_FMT);
              const muted = !d.isSame(view, "month");
              const end = isEnd(d);
              const mid = inRange(d) && !end;
              const disabled = isDisabled(d);
              return (
                <button
                  key={iso}
                  type="button"
                  role="gridcell"
                  data-date={iso}
                  tabIndex={iso === focusedISO ? 0 : -1}
                  aria-selected={end || mid}
                  aria-disabled={disabled}
                  disabled={disabled}
                  aria-label={d.format("dddd, D MMMM YYYY")}
                  onClick={() => !disabled && onPick(d.clone())}
                  style={{
                    height: 30,
                    border: 0,
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: disabled ? "not-allowed" : "pointer",
                    fontFamily: "var(--font-mono)",
                    background: end ? "var(--ink-700, #1f2937)" : mid ? "var(--ink-50, #eef2ff)" : "transparent",
                    color: disabled ? "var(--fg-subtle)" : end ? "#fff" : muted ? "var(--fg-subtle)" : "var(--fg-default)",
                    fontWeight: end ? 700 : 400,
                    opacity: disabled ? 0.4 : 1,
                  }}
                >
                  {d.date()}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Popover shell for the calendar filters ----------
   Wraps the trigger + a portalled Radix popover so the panel escapes any
   `overflow:hidden`/scroll clipping, flips to stay on-screen, moves focus in on
   open / back to the trigger on close, and dismisses on Escape or outside click.
   Focus on open is handed to the calendar's active day (Radix's own autofocus is
   suppressed) rather than the month chevron. */
const CalFilterShell = ({ label, triggerText, active, title, dialogLabel, open, onOpenChange, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", maxWidth: "100%" }}>
    <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>{label}</span>
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          title={title}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            height: 28,
            padding: "0 10px",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            fontSize: 12,
            fontFamily: "var(--font-sans)",
            background: "var(--bg-panel)",
            color: active ? "var(--fg-default)" : "var(--fg-muted)",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <Calendar size={14} strokeWidth={1.6} />
          {triggerText}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          role="dialog"
          aria-label={dialogLabel}
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          style={{
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
            padding: 10,
            /* Portalled to <body>, so it must out-rank the drawers/overlays it
               can open from (z-index 60) — and the task center (1100) — or it
               renders behind them and looks "not editable". */
            zIndex: 1200,
          }}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  </div>
);

export const DateRangeFilter = ({ from, to, onFromChange, onToChange, label = "Date range" }) => {
  const [open, setOpen] = React.useState(false);

  const fromM = from ? moment(from, DATE_FMT) : null;
  const toM = to ? moment(to, DATE_FMT) : null;

  const pick = (day) => {
    const d = day.format(DATE_FMT);
    /* No start yet, or a complete range exists -> begin a fresh range. */
    if (!fromM || (fromM && toM)) {
      onFromChange(d);
      onToChange("");
      return;
    }
    /* Start set, end open -> set end, swapping if the user picked an earlier day. */
    if (day.isBefore(fromM, "day")) {
      onToChange(fromM.format(DATE_FMT));
      onFromChange(d);
    } else {
      onToChange(d);
    }
    setOpen(false);
  };

  const clear = () => {
    onFromChange("");
    onToChange("");
    setOpen(false);
  };

  const triggerText = !fromM
    ? "All dates"
    : toM
    ? `${fromM.format("DD MMM YY")} – ${toM.format("DD MMM YY")}`
    : `${fromM.format("DD MMM YY")} – …`;

  return (
    <CalFilterShell
      label={label}
      triggerText={triggerText}
      active={!!fromM}
      title="Select a date range"
      dialogLabel="Choose a date range"
      open={open}
      onOpenChange={setOpen}
    >
      <div style={{ width: 252 }}>
        <CalendarGrid seedISO={from || ""} fromM={fromM} toM={toM} onPick={pick} />

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
          <span aria-live="polite" style={{ fontSize: 10, color: "var(--fg-muted)" }}>
            {fromM && !toM ? "Pick an end date" : "Pick a start date"}
          </span>
          <button
            type="button"
            onClick={clear}
            style={{
              border: 0,
              background: "transparent",
              color: "var(--fg-link)",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              padding: "2px 4px",
            }}
          >
            Clear
          </button>
        </div>
      </div>
    </CalFilterShell>
  );
};

const CalNavBtn = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 24,
      height: 24,
      border: "1px solid var(--border-default)",
      borderRadius: 4,
      background: "var(--bg-panel)",
      color: "var(--fg-default)",
      cursor: "pointer",
    }}
  >
    {children}
  </button>
);

/* ---------- Bounded 12-hour time field ----------
   A native <input type="time"> spinner wraps endlessly (…59 → 00 → 01…), which
   reads as "infinite scrolling". This replaces it with three finite scroll
   columns — hour 01–12, minute 00–59, AM/PM — so each list has hard ends.
   Value in/out stays 24h "HH:mm" so the surrounding filter contract is
   unchanged; `max` (also "HH:mm") caps the selection for today. */
const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const MERIDIEM = ["AM", "PM"];

const parse12 = (hhmm) => {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return { h: "", m: "", ap: "AM" };
  const [H, M] = hhmm.split(":").map(Number);
  const ap = H >= 12 ? "PM" : "AM";
  const h = H % 12 === 0 ? 12 : H % 12;
  return { h: String(h).padStart(2, "0"), m: String(M).padStart(2, "0"), ap };
};

const compose24 = (h, m, ap) => {
  let H = Number(h) % 12;
  if (ap === "PM") H += 12;
  return `${String(H).padStart(2, "0")}:${m}`;
};

const TimeField = ({ value, onChange, ariaLabel, max }) => {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const { h, m, ap } = parse12(value);

  /* Close when a pointer lands outside the field/dropdown. */
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open]);

  const commit = (nh, nm, nap) => {
    const next = compose24(nh || "12", nm || "00", nap || "AM");
    onChange(max && next > max ? max : next); // clamp future times on today
  };

  const label = value ? `${h}:${m} ${ap}` : "--:-- --";

  const col = (items, selected, onSelect, last) => (
    <div
      style={{
        height: 132,
        overflowY: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: 2,
        borderRight: last ? 0 : "1px solid var(--border-subtle)",
      }}
    >
      {items.map((it) => {
        const on = it === selected;
        return (
          <button
            key={it}
            type="button"
            onClick={() => onSelect(it)}
            style={{
              flex: "0 0 auto",
              height: 24,
              border: 0,
              borderRadius: 4,
              fontSize: 12,
              fontFamily: "var(--font-mono)",
              cursor: "pointer",
              background: on ? "var(--ink-700, #1f2937)" : "transparent",
              color: on ? "#fff" : "var(--fg-default)",
              fontWeight: on ? 700 : 400,
            }}
          >
            {it}
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        style={{
          height: 28,
          padding: "0 8px",
          border: "1px solid var(--border-default)",
          borderRadius: 4,
          fontSize: 12,
          fontFamily: "var(--font-sans)",
          background: "var(--bg-panel)",
          color: value ? "var(--fg-default)" : "var(--fg-muted)",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        <span>{label}</span>
        <Clock size={14} strokeWidth={1.6} />
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: 32,
            left: 0,
            right: 0,
            zIndex: 5,
            display: "flex",
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
          }}
        >
          {col(HOURS_12, h, (v) => commit(v, m, ap), false)}
          {col(MINUTES_60, m, (v) => commit(h, v, ap), false)}
          {col(MERIDIEM, ap, (v) => commit(h, m, v), true)}
        </div>
      )}
    </div>
  );
};

/* ---------- Date + time range filter ----------
   One popover that captures a full window: a calendar (left) for the start/end
   dates and a time sidebar (right) for the start/end times. Selection is held in
   an internal draft and only pushed out — via a single `onApply({from,to,
   fromTime,toTime})` — when the operator clicks Apply, so the API fires once for
   the complete window rather than on every partial change. `from`/`to` are
   YYYY-MM-DD, `fromTime`/`toTime` are HH:mm; the same shapes come back on apply. */
export const DateTimeRangeFilter = ({
  from,
  to,
  fromTime = "",
  toTime = "",
  onApply,
  label = "Date & time",
}) => {
  const [open, setOpen] = React.useState(false);

  /* Draft state — reseeded from props each time the popover opens. */
  const [dFrom, setDFrom] = React.useState(from || "");
  const [dTo, setDTo] = React.useState(to || "");
  const [dFromTime, setDFromTime] = React.useState(fromTime || "");
  const [dToTime, setDToTime] = React.useState(toTime || "");

  const fromM = dFrom ? moment(dFrom, DATE_FMT) : null;
  const toM = dTo ? moment(dTo, DATE_FMT) : null;

  /* Today is the latest selectable day; future dates/times are blocked. */
  const todayM = moment().startOf("day");
  const nowTime = moment().format("HH:mm");

  const onOpenChange = (o) => {
    /* Reseed the draft from the applied props whenever we open. */
    if (o) {
      setDFrom(from || "");
      setDTo(to || "");
      setDFromTime(fromTime || "");
      setDToTime(toTime || "");
    }
    setOpen(o);
  };

  const pick = (day) => {
    if (day.isAfter(todayM, "day")) return; // no future dates
    const d = day.format(DATE_FMT);
    if (!fromM || (fromM && toM)) {
      setDFrom(d);
      setDTo("");
      return;
    }
    if (day.isBefore(fromM, "day")) {
      setDTo(fromM.format(DATE_FMT));
      setDFrom(d);
    } else {
      setDTo(d);
    }
  };

  const clear = () => {
    setDFrom("");
    setDTo("");
    setDFromTime("");
    setDToTime("");
    onApply && onApply({ from: "", to: "", fromTime: "", toTime: "" });
    setOpen(false);
  };

  const apply = () => {
    onApply &&
      onApply({ from: dFrom, to: dTo, fromTime: dFromTime, toTime: dToTime });
    setOpen(false);
  };

  /* Apply needs a complete date window; times are optional. */
  const canApply = !!dFrom && !!dTo;

  const timeSuffix = (t) => (t ? ` ${t}` : "");
  const appliedFromM = from ? moment(from, DATE_FMT) : null;
  const appliedToM = to ? moment(to, DATE_FMT) : null;
  const triggerText = !appliedFromM
    ? "All dates"
    : appliedToM
    ? `${appliedFromM.format("DD MMM YY")}${timeSuffix(fromTime)} – ${appliedToM.format("DD MMM YY")}${timeSuffix(toTime)}`
    : `${appliedFromM.format("DD MMM YY")}${timeSuffix(fromTime)} – …`;

  return (
    <CalFilterShell
      label={label}
      triggerText={triggerText}
      active={!!appliedFromM}
      title="Select a date & time range"
      dialogLabel="Choose a date and time range"
      open={open}
      onOpenChange={onOpenChange}
    >
      <div style={{ display: "flex", gap: 12 }}>
        {/* Calendar */}
        <div style={{ width: 252 }}>
          <CalendarGrid seedISO={from || ""} fromM={fromM} toM={toM} onPick={pick} maxM={todayM} />
          <div aria-live="polite" style={{ marginTop: 8, fontSize: 10, color: "var(--fg-muted)" }}>
            {fromM && !toM ? "Pick an end date" : "Pick a start date"}
          </div>
        </div>

        {/* Time sidebar */}
        <div
          style={{
            width: 150,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            borderLeft: "1px solid var(--border-subtle)",
            paddingLeft: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-strong)", marginBottom: 4 }}>
              Start
            </div>
            <div style={{ fontSize: 10, color: "var(--fg-muted)", marginBottom: 4 }}>
              {fromM ? fromM.format("DD MMM YYYY") : "—"}
            </div>
            <TimeField
              value={dFromTime}
              onChange={setDFromTime}
              ariaLabel="Start time"
              max={fromM && fromM.isSame(todayM, "day") ? nowTime : undefined}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-strong)", marginBottom: 4 }}>
              End
            </div>
            <div style={{ fontSize: 10, color: "var(--fg-muted)", marginBottom: 4 }}>
              {toM ? toM.format("DD MMM YYYY") : "—"}
            </div>
            <TimeField
              value={dToTime}
              onChange={setDToTime}
              ariaLabel="End time"
              max={toM && toM.isSame(todayM, "day") ? nowTime : undefined}
            />
          </div>

          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              type="button"
              onClick={apply}
              disabled={!canApply}
              style={{
                height: 30,
                border: 0,
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "var(--font-sans)",
                background: canApply ? "var(--ink-700, #1f2937)" : "var(--bg-sunken)",
                color: canApply ? "#fff" : "var(--fg-subtle)",
                cursor: canApply ? "pointer" : "not-allowed",
              }}
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clear}
              style={{
                border: 0,
                background: "transparent",
                color: "var(--fg-link)",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                padding: "2px 4px",
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </CalFilterShell>
  );
};

/* ---------- Eyebrow label ---------- */
export const Eyebrow = ({ children, style = {} }) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "var(--fg-muted)",
      ...style,
    }}
  >
    {children}
  </span>
);

/* ---------- Section / page header ---------- */
export const SectionHeader = ({ title, sub, actions }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
    <div style={{ minWidth: 0 }}>
      <h2
        style={{
          margin: 0,
          fontSize: "var(--t-2xl)",
          fontWeight: 600,
          color: "var(--fg-strong)",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
      {sub && <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 3 }}>{sub}</div>}
    </div>
    {actions && <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{actions}</div>}
  </div>
);

/* ---------- Toolbar ---------- */
export const ToolbarBar = ({ children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      padding: "8px 12px",
      border: "1px solid var(--border-default)",
      borderRadius: 6,
      background: "var(--bg-panel)",
    }}
  >
    {children}
  </div>
);

/* ---------- Tab bar (count chips) ---------- */
export const TabBar = ({ tabs, value, onChange }) => (
  <div
    style={{
      display: "flex",
      gap: 0,
      borderBottom: "1px solid var(--border-default)",
      overflowX: "auto",
    }}
    className="cb-scroll"
  >
    {tabs.map((t) => {
      const active = value === t.id;
      const chipBg =
        t.alert === "amber"
          ? "var(--amber-50)"
          : t.alert === "red"
          ? "var(--red-50)"
          : active
          ? "var(--ink-50)"
          : "var(--bg-sunken)";
      const chipFg =
        t.alert === "amber"
          ? "var(--amber-700)"
          : t.alert === "red"
          ? "var(--red-700)"
          : active
          ? "var(--ink-700)"
          : "var(--fg-muted)";
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: "8px 14px",
            border: 0,
            cursor: "pointer",
            background: "transparent",
            fontSize: 12,
            fontWeight: 600,
            color: active ? "var(--ink-700)" : "var(--fg-muted)",
            borderBottom: active ? "2px solid var(--ink-600)" : "2px solid transparent",
            marginBottom: -1,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {t.label}
          {t.count !== undefined && t.count !== null && (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                fontWeight: 600,
                padding: "1px 5px",
                borderRadius: 99,
                background: chipBg,
                color: chipFg,
              }}
            >
              {t.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

/* ---------- Segmented control ---------- */
export const Segmented = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 0, border: "1px solid var(--border-default)", borderRadius: 4, overflowX: "auto", maxWidth: "100%" }}>
    {options.map((o) => {
      const active = value === o.id;
      /* An option can be disabled (with an optional `title` explaining why) —
         used for choices that only some records support, e.g. loose-unit sale. */
      return (
        <button
          key={o.id}
          onClick={() => !o.disabled && onChange(o.id)}
          disabled={o.disabled}
          title={o.title}
          style={{
            padding: "0 12px",
            height: 28,
            border: 0,
            cursor: o.disabled ? "not-allowed" : "pointer",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 600,
            background: active ? "var(--ink-50)" : "var(--bg-panel)",
            color: o.disabled ? "var(--fg-subtle)" : active ? "var(--ink-700)" : "var(--fg-default)",
            borderRight: "1px solid var(--border-default)",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

/* ---------- KPI box ---------- */
export const KpiBox = ({ label, value, sub, variant = "ink" }) => {
  const tones = {
    ink: { fg: "var(--ink-700)", bg: "var(--ink-50)", border: "var(--ink-100)" },
    green: { fg: "var(--green-700)", bg: "var(--green-50)", border: "var(--green-100)" },
    amber: { fg: "var(--amber-700)", bg: "var(--amber-50)", border: "var(--amber-100)" },
    red: { fg: "var(--red-700)", bg: "var(--red-50)", border: "var(--red-100)" },
  };
  const c = tones[variant] || tones.ink;
  return (
    <div
      style={{
        background: "var(--bg-panel)",
        border: `1px solid ${c.border}`,
        borderRadius: 6,
        padding: "10px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        boxShadow: `inset 3px 0 0 ${c.fg}`,
      }}
    >
      <Eyebrow>{label}</Eyebrow>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontVariantNumeric: "tabular-nums",
          fontSize: 20,
          fontWeight: 700,
          color: c.fg,
          lineHeight: 1.1,
        }}
      >
        {value}
      </span>
      {sub && <span style={{ fontSize: 10, color: "var(--fg-muted)" }}>{sub}</span>}
    </div>
  );
};

/* ---------- Key/value field ---------- */
export const KV = ({ label, value, mono, small }) => (
  <div>
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: "var(--fg-muted)",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
        fontSize: small ? 11 : 13,
        fontWeight: small ? 500 : 600,
        color: small ? "var(--fg-default)" : "var(--fg-strong)",
        marginTop: 2,
        wordBreak: "break-word",
      }}
    >
      {value}
    </div>
  </div>
);

/* ---------- Data table ---------- */
export const Table = ({ columns = [], children, sticky = false }) => (
  <div
    style={{
      background: "var(--bg-panel)",
      border: "1px solid var(--border-default)",
      borderRadius: 6,
      overflow: "auto",
    }}
    className="cb-scroll"
  >
    <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "auto" }}>
      <thead style={sticky ? { position: "sticky", top: 0, zIndex: 2 } : null}>
        <tr>
          {columns.map((c, i) => (
            <th
              key={i}
              style={{
                background: "var(--bg-subtle)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-muted)",
                textAlign: c.align || "left",
                padding: "7px 12px",
                borderBottom: "1px solid var(--border-default)",
                whiteSpace: "nowrap",
                width: c.width,
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const Tr = ({ children, selected, onClick, style = {} }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <tr
      onClick={onClick}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      style={{
        background: selected ? "var(--bg-selected)" : hover ? "var(--bg-hover)" : "transparent",
        boxShadow: selected ? "inset 2px 0 0 var(--ink-600)" : "none",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </tr>
  );
};

export const Td = ({ children, mono, right, style = {}, ...rest }) => (
  <td
    {...rest}
    style={{
      padding: "0 12px",
      height: 32,
      borderBottom: "1px solid var(--border-subtle)",
      color: "var(--fg-default)",
      fontSize: 12,
      verticalAlign: "middle",
      fontFamily: mono ? "var(--font-mono)" : "inherit",
      fontFeatureSettings: mono ? '"tnum","zero"' : "normal",
      textAlign: right ? "right" : "left",
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </td>
);

/* ---------- Empty state ---------- */
export const EmptyState = ({ icon, title, hint }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      textAlign: "center",
      padding: "48px 24px",
      color: "var(--fg-muted)",
    }}
  >
    <span style={{ color: "var(--fg-subtle)" }}>{icon || <Inbox size={28} strokeWidth={1.5} />}</span>
    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-strong)" }}>{title}</div>
    {hint && <div style={{ fontSize: 12, maxWidth: 360 }}>{hint}</div>}
  </div>
);

/* ---------- Loading state ---------- */
export const LoadingState = ({ label = "Loading…" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "56px 24px",
      color: "var(--fg-muted)",
      fontSize: 13,
    }}
  >
    <Loader2 size={18} strokeWidth={2} style={{ animation: "spin 1s linear infinite" }} className="animate-spin" />
    {label}
  </div>
);

/* ---------- Error state ---------- */
export const ErrorState = ({ title = "Couldn't load this screen", message, onRetry }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "48px 24px",
      textAlign: "center",
      border: "1px solid var(--red-100)",
      borderRadius: 6,
      background: "var(--bg-panel)",
    }}
  >
    <span style={{ color: "var(--red-500)" }}>
      <AlertTriangle size={28} strokeWidth={1.6} />
    </span>
    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-strong)" }}>{title}</div>
    <div style={{ fontSize: 12, color: "var(--fg-muted)", maxWidth: 400 }}>
      {message || "Something went wrong while fetching data. Check your connection and try again."}
    </div>
    {onRetry && (
      <Button variant="primary" onClick={onRetry} style={{ marginTop: 4 }}>
        Retry
      </Button>
    )}
  </div>
);

/* ---------- Inline alert (errors inside forms / panels) ---------- */
export const InlineAlert = ({ tone = "red", children }) => {
  const tones = {
    red: { bg: "var(--red-50)", fg: "var(--red-700)" },
    amber: { bg: "var(--amber-50)", fg: "var(--amber-700)" },
    green: { bg: "var(--green-50)", fg: "var(--green-700)" },
  };
  const c = tones[tone] || tones.red;
  if (!children) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 4,
        background: c.bg,
        color: c.fg,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <AlertTriangle size={13} strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
};

/* ============================================================
   Shared field validators — used across the Supply screens so
   email / phone checks stay consistent module-wide.
   Each returns null when valid, or an error string when not.
   Empty input is treated as valid (use a separate required check).
   ============================================================ */

// Pragmatic RFC-5322-ish check: local@domain.tld, no spaces.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emailError = (value) => {
  const raw = (value || "").trim();
  if (!raw) return null; // optional unless caller enforces required
  return EMAIL_RE.test(raw) ? null : "Enter a valid email address.";
};

// Indian 10-digit mobile, tolerating spaces/dashes and an optional +91.
export const phoneError = (value) => {
  const raw = (value || "").trim();
  if (!raw) return null;
  const digits = raw.replace(/[\s-]/g, "").replace(/^\+91/, "");
  return /^[6-9]\d{9}$/.test(digits) ? null : "Enter a valid 10-digit mobile number.";
};
